import type React from 'react';
import Button from '../Button';
import CheckIcon from '../CustomIcons/CheckIcon';
import SparkleIcon from '../CustomIcons/SparkleIcon';
import { Typography } from '../Typography';

export interface ChangeReviewSummaryProps {
    /** How many changes are still undecided. */
    count: number;
    /** What made them. Names the assistant by default. */
    origin?: string;
    onApproveAll: () => void;
    onDenyAll: () => void;
    disabled?: boolean;
    /**
     * What to say once nothing is outstanding. Omit and the bar renders nothing, which is what
     * a caller wants when the space should collapse rather than reassure.
     */
    settledMessage?: string;
    className?: string;
}

/**
 * How many machine-made changes are still waiting on a decision, and the two ways to settle
 * all of them at once.
 *
 * The count is the point: a per-field control cannot tell you that something is pending in a
 * tab you are not looking at, and this is what blocks a save until the number reaches zero.
 */
const ChangeReviewSummary: React.FC<ChangeReviewSummaryProps> = ({
    count,
    origin = 'assistant',
    onApproveAll,
    onDenyAll,
    disabled = false,
    settledMessage,
    className = '',
}) => {
    if (count <= 0) {
        if (settledMessage === undefined) {
            return null;
        }

        return (
            <div className={`flex items-center gap-2 ${className}`}>
                <CheckIcon size={12} />
                <Typography.SecondaryText size="small">{settledMessage}</Typography.SecondaryText>
            </div>
        );
    }

    return (
        <div
            className={`flex flex-wrap items-center gap-2 rounded-lg p-2 ${className}`}
            style={{ background: 'var(--malachite-tag-assistant-background)' }}
        >
            {/* Coloured explicitly: the glyph inherits `currentColor`, which on this bar is
                the default text colour, so it drew near-black beside its own violet label. */}
            <SparkleIcon
                size={12}
                style={{ color: 'var(--malachite-assistant-foreground)' }}
            />
            <Typography.Text
                size="small"
                fontWeight="semi-bold"
                style={{ color: 'var(--malachite-assistant-foreground)' }}
            >
                {`${count} ${count === 1 ? 'change' : 'changes'} from the ${origin} ${
                    count === 1 ? 'needs' : 'need'
                } review`}
            </Typography.Text>
            <div className="ml-auto flex items-center gap-2">
                {/* The same glyphs the per-field controls carry, so the bar reads as the
                    bulk form of those two actions rather than as a separate pair. The gap is
                    a margin, not a space: Button lays its children out with flex, which
                    strips the leading whitespace off a bare text node. */}
                {/* `surface` for the same reason the per-field Revert uses it: the bar has its
                    own violet ground, and a transparent button on it stops reading as one. */}
                <Button variant="surface" size="small" disabled={disabled} onClick={onDenyAll}>
                    <span aria-hidden="true" className="mr-1">
                        ↺
                    </span>
                    Revert all
                </Button>
                <Button variant="filled" size="small" disabled={disabled} onClick={onApproveAll}>
                    <span aria-hidden="true" className="mr-1">
                        ✓
                    </span>
                    Accept all
                </Button>
            </div>
        </div>
    );
};

export default ChangeReviewSummary;
export { ChangeReviewSummary };
